import React, { memo, useCallback, useMemo } from 'react';
import { Badge, Box, Paper, Tab, Tabs } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useIntl } from 'react-intl';
import { useFeatureFlag } from 'modules/common/hooks';
import { SmartDiaryListTabValue } from './hooks/useSmartDiaryTabs';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2, 1, 1),
    width: '100%'
  },
  tab: {
    minWidth: 'calc(100% / 3)'
  },
  tabLabelContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  tabLabelText: {
    paddingLeft: theme.spacing(1.5)
  }
}));

const useTabStyles = makeStyles((theme) => ({
  root: {
    fontWeight: theme.typography.fontWeightRegular
  },
  textColorInherit: {
    color: theme.palette.text.secondary,
    opacity: 1,
    textTransform: 'none',
    ...theme.typography.body1
  },
  selected: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    opacity: 1
  }
}));

export interface SmartDiaryListTabsProps {
  tabValue: SmartDiaryListTabValue;
  setTabValue: (value: SmartDiaryListTabValue) => void;
  appliedCount: number;
  notAppliedCount: number;
  hasTabUpdate: boolean;
}

export interface SmartDiaryListTab {
  label: string;
  value: SmartDiaryListTabValue;
}

export const SmartDiaryListTabs: React.FC<SmartDiaryListTabsProps> = memo(
  ({
    tabValue,
    setTabValue,
    notAppliedCount,
    appliedCount,
    hasTabUpdate
  }: SmartDiaryListTabsProps) => {
    const classes = useStyles();
    const tabClasses = useTabStyles();
    const { formatMessage } = useIntl();
    const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
    const handleChange = useCallback(
      (_: any, value: SmartDiaryListTabValue) => {
        setTabValue(value);
      },
      [setTabValue]
    );

    const createLabel = useCallback(
      (messageID: string, tabCount: number) => {
        return `${formatMessage({
          id: messageID
        })} (${tabCount})`;
      },
      [formatMessage]
    );

    const tabs: SmartDiaryListTab[] = useMemo(
      () => [
        {
          label: createLabel('smartDiary.tabs.notApplied', notAppliedCount),
          value: SmartDiaryListTabValue.NOT_APPLIED
        },
        {
          label: createLabel('smartDiary.tabs.applied', appliedCount),
          value: SmartDiaryListTabValue.APPLIED
        },
        {
          label: createLabel(
            'smartDiary.tabs.all',
            appliedCount + notAppliedCount
          ),
          value: SmartDiaryListTabValue.ALL
        }
      ],
      [appliedCount, createLabel, notAppliedCount]
    );

    const tabIndicatorProps = useMemo(() => {
      const tabIndex = tabs.findIndex((tab) => tab.value === tabValue);
      return {
        style: {
          left: `calc((100% / 3) * ${tabIndex})`,
          width: 'calc(100% / 3)'
        }
      };
    }, [tabValue, tabs]);
    return (
      <Box
        className={classes.root}
        data-testid="smart-diary-list-tabs-container"
      >
        <Paper square>
          <Tabs
            value={tabValue}
            textColor="inherit"
            indicatorColor="primary"
            onChange={handleChange}
            data-testid="smart-diary-list-tabs"
            TabIndicatorProps={tabIndicatorProps}
            selectionFollowsFocus={isTiwScreenReaderEnabled ? false : true}
          >
            {tabs.map((tab: SmartDiaryListTab) => (
              <Tab
                className={classes.tab}
                key={tab.value}
                label={
                  tab.value !== SmartDiaryListTabValue.APPLIED ? (
                    <Box className={classes.tabLabelContainer}>
                      <Badge
                        variant={'dot'}
                        color="primary"
                        invisible={!hasTabUpdate}
                        data-testid={`smart-diary-list-tab-badge_${tab.value}`}
                      />
                      <span
                        data-testid={`smart-diary-list-tab-label_${tab.value}`}
                        className={classes.tabLabelText}
                      >
                        {tab.label}
                      </span>
                    </Box>
                  ) : (
                    <span
                      data-testid={`smart-diary-list-tab-label_${tab.value}`}
                      className={classes.tabLabelText}
                    >
                      {tab.label}
                    </span>
                  )
                }
                value={tab.value}
                classes={tabClasses}
                data-testid={`smart-diary-list-tab_${tab.value}`}
              />
            ))}
          </Tabs>
        </Paper>
      </Box>
    );
  }
);

SmartDiaryListTabs.displayName = 'SmartDiaryListTabs';
